/**
 * ExpensesSection Component
 * Wrapper section that shows expense list with add/upload capabilities.
 * Used in property and unit detail pages.
 */

import { useState, useEffect } from 'react';
import { expensesApi, expenseCategoriesApi } from '../../lib/api';
import { ExpenseList } from './ExpenseList';
import { ExpenseForm } from './ExpenseForm';
import { ExpenseUploadDropzone } from './ExpenseUploadDropzone';
import { Modal } from '../Modal';
import { PageSection } from '../layout/index';
import { Button } from '../ui/button';
import type {
  ExpenseCategory,
  ExpenseDetail,
  CreateExpenseBody,
  UpdateExpenseBody,
} from '../../types/expense.types';

interface ExpensesSectionProps {
  propertyId: string;
  unitId?: string;
}

type CreateMode = 'none' | 'choose' | 'manual' | 'upload';

export function ExpensesSection({ propertyId, unitId }: ExpensesSectionProps) {
  const [createMode, setCreateMode] = useState<CreateMode>('none');
  const [isCreating, setIsCreating] = useState(false);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  useEffect(() => {
    expenseCategoriesApi.list()
      .then((res) => setCategories(res.categories))
      .catch(() => { /* categories will be empty, form still works */ });
  }, []);

  const handleCreateSubmit = async (values: CreateExpenseBody | UpdateExpenseBody) => {
    setIsCreating(true);
    try {
      await expensesApi.create(values as CreateExpenseBody);
      setCreateMode('none');
      setListRefreshKey((k) => k + 1);
    } finally {
      setIsCreating(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUploadSuccess = (_expense: ExpenseDetail) => {
    setCreateMode('none');
    setListRefreshKey((k) => k + 1);
  };

  return (
    <>
      <PageSection
        title="Expenses"
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCreateMode('choose')}
            data-testid="expense-create-button"
            aria-label="Add expense"
          >
            + Add Expense
          </Button>
        }
      >
        <div className="px-[var(--page-padding-x)] md:px-4 pb-3">
          <ExpenseList
            key={listRefreshKey}
            propertyId={propertyId}
            unitId={unitId}
          />
        </div>
      </PageSection>

      {/* Choose Method Modal */}
      <Modal
        isOpen={createMode === 'choose'}
        onClose={() => setCreateMode('none')}
        title="Add Expense"
      >
        <p className="m-0 mb-4 text-sm text-muted-foreground">
          How would you like to add this expense?
        </p>
        <div className="flex flex-col gap-3">
          <button
            type="button"
            className="rounded-lg p-4 border-2 border-border bg-background cursor-pointer text-left hover:border-primary transition-colors"
            onClick={() => setCreateMode('upload')}
            aria-label="Upload a receipt or invoice for AI processing"
          >
            <div className="text-base font-semibold text-foreground mb-1">
              Upload Receipt / Invoice
            </div>
            <div className="text-[13px] text-muted-foreground">
              Upload a PDF, image, or document. AI will extract the expense details automatically.
            </div>
          </button>
          <button
            type="button"
            className="rounded-lg p-4 border-2 border-border bg-background cursor-pointer text-left hover:border-primary transition-colors"
            onClick={() => setCreateMode('manual')}
            data-testid="expense-create-manual"
            aria-label="Enter expense details manually"
          >
            <div className="text-base font-semibold text-foreground mb-1">
              Enter Manually
            </div>
            <div className="text-[13px] text-muted-foreground">
              Fill in the expense details yourself — amount, date, vendor, category, etc.
            </div>
          </button>
        </div>
      </Modal>

      {/* Create Expense Modal */}
      <Modal
        isOpen={createMode === 'manual'}
        onClose={() => setCreateMode('none')}
        title="Add Expense"
        width="large"
      >
        <ExpenseForm
          mode="create"
          propertyId={propertyId}
          unitId={unitId}
          categories={categories}
          onSubmit={handleCreateSubmit}
          onCancel={() => setCreateMode('none')}
          isLoading={isCreating}
        />
      </Modal>

      {/* Upload Receipt Modal */}
      <Modal
        isOpen={createMode === 'upload'}
        onClose={() => setCreateMode('none')}
        title="Upload Receipt or Invoice"
        width="large"
      >
        <p className="m-0 mb-4 text-sm text-muted-foreground">
          Upload a PDF or image. We'll extract the expense details automatically.
        </p>
        <ExpenseUploadDropzone
          propertyId={propertyId}
          unitId={unitId}
          onSuccess={handleUploadSuccess}
        />
      </Modal>
    </>
  );
}

export default ExpensesSection;
